import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCalendarRange, LuCircleArrowDown, LuCircleCheck, LuHourglass, LuTrash2, LuWalletCards } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { TextInput } from '../../components/form/FormControls';
import { useTheme } from '../../contexts/ThemeContext';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import AddIncomeModal from './AddIncomeModal';
import AddExpenseModal from './AddExpenseModal';
import ImportIncomeExcelModal from './ImportIncomeExcelModal';
import ImportExpenseExcelModal from './ImportExpenseExcelModal';
import EditIncomeModal from './EditIncomeModal';
import EditExpenseModal from './EditExpenseModal';
import useBudgets from './useBudgets';
import useBudgetText from './budgetText';
import {
  ALL_COLUMNS,
  EXPENSE_COLUMNS,
  INCOME_COLUMNS,
  MIN_COLUMN_WIDTH,
  defaultVisible,
  getFilterState,
  pinnedFor,
} from './budgetColumns';
import BudgetHeader from './parts/BudgetHeader';
import BudgetCell, { BudgetCardRow, makeCellTitle } from './parts/BudgetCell';
import BudgetDetailPane from './parts/BudgetDetailPane';
import BudgetCharts from './parts/BudgetCharts';

const INCOME_DEFAULTS = defaultVisible(INCOME_COLUMNS);
const EXPENSE_DEFAULTS = defaultVisible(EXPENSE_COLUMNS);
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;
// Date range pseudo filters (kept in their own state by useBudgets)
const START_KEY = '__startDate';
const END_KEY = '__endDate';

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Gelir & Gider Yönetimi (Tailwind), built like Çalışan / Müşteri Yönetimi:
 * amount tiles, the Gelir / Gider lists as a switch in a table card that
 * reaches at most the bottom of the screen (header filters, date range,
 * column menu, "go to page"), the selected record's panel beside the table
 * and the statistics below. The data logic lives in useBudgets.
 */
const BudgetManagementPage = () => {
  const { isDarkMode } = useTheme();
  const {
    isAdmin, isLoading, budgets, activeTab, setActiveTab,
    filteredIncome, filteredExpenses, filters, handleFilter, clearFilters,
    searchTerm, setSearchTerm, startDateFilter, setStartDateFilter, endDateFilter, setEndDateFilter, isAnyFilterActive,
    pendingBudgetAmount, paidBudgetAmount, receivedIncomeAmount, pendingIncomeAmount,
    selectedBudget, setSelectedBudget, handleBudgetSelect, canToggleStatus,
    handleToggleStatus, handleEditBudget, handleDeleteBudget, handleToggleActiveStatus,
    deleteConfirm, setDeleteConfirm, confirmDelete, deleteAllConfirm, setDeleteAllConfirm, confirmDeleteAll, isDeletingAll,
    isAddIncomeModalOpen, setIsAddIncomeModalOpen, isAddExpenseModalOpen, setIsAddExpenseModalOpen,
    isImportIncomeModalOpen, setIsImportIncomeModalOpen, isImportExpenseModalOpen, setIsImportExpenseModalOpen,
    isEditIncomeModalOpen, setIsEditIncomeModalOpen, isEditExpenseModalOpen, setIsEditExpenseModalOpen,
    editingBudget, setEditingBudget, handleAddItem, handleUpdateBudget,
    incomeUniqueValues, expenseUniqueValues, getEmployeeName,
    fetchBudgets, downloadExcel,
  } = useBudgets();
  const { language, locale, label, statusLabel, formatDate } = useBudgetText();

  const incomeColumns = useTableColumns({ storageKey: 'budget.income.tableColumns.v1', columns: INCOME_COLUMNS, defaultVisible: INCOME_DEFAULTS, minWidth: MIN_COLUMN_WIDTH });
  const expenseColumns = useTableColumns({ storageKey: 'budget.expense.tableColumns.v1', columns: EXPENSE_COLUMNS, defaultVisible: EXPENSE_DEFAULTS, minWidth: MIN_COLUMN_WIDTH });
  const isIncomeTab = activeTab === 'gelir';
  const columns = isIncomeTab ? incomeColumns : expenseColumns;
  const columnDefs = isIncomeTab ? INCOME_COLUMNS : EXPENSE_COLUMNS;
  const rows = isIncomeTab ? filteredIncome : filteredExpenses;
  const uniqueValues = isIncomeTab ? incomeUniqueValues : expenseUniqueValues;

  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const hasLoaded = budgets.length > 0 || !isLoading;
  const isFilterActive = Boolean(isAnyFilterActive);

  // Süzgeç, arama, tarih aralığı ya da sekme değişince ilk sayfaya dönülür
  const resetKey = JSON.stringify([filters, searchTerm, startDateFilter, endDateFilter, activeTab]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(rows, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedBudget) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedBudget(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedBudget, showFilterSheet, setSelectedBudget]);

  const columnLabel = useCallback((key) => label(`columns.${ALL_COLUMNS[key].id}`), [label]);
  const optionsFor = useCallback((key) => (uniqueValues[key] || []).map((option) => ({
    value: option.value,
    label: key === 'ÖdemeDurumu' ? statusLabel(option.value) : option.label,
  })), [uniqueValues, statusLabel]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: columnDefs[key].type, options: columnDefs[key].type === 'select' ? optionsFor(key) : undefined },
  })), [columns.visibleKeys, columnDefs, columnLabel, optionsFor]);

  const chipValue = (key, value) => {
    if (key === 'ÖdemeDurumu') return statusLabel(value);
    if (ALL_COLUMNS[key]?.type === 'date') return formatDate(value);
    return String(value);
  };
  // Filters are shared by both lists (as before), so every one in use shows a chip
  const filterChips = [
    ...Object.keys(filters)
      .filter((key) => ALL_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
      .map((key) => ({ key, label: columnLabel(key), value: chipValue(key, filters[key]), state: 'applied' })),
    ...(startDateFilter ? [{ key: START_KEY, label: label('toolbar.startDate'), value: formatDate(startDateFilter), state: 'applied' }] : []),
    ...(endDateFilter ? [{ key: END_KEY, label: label('toolbar.endDate'), value: formatDate(endDateFilter), state: 'applied' }] : []),
  ];

  const changeFilter = (key, value) => {
    if (key === START_KEY) setStartDateFilter(value);
    else if (key === END_KEY) setEndDateFilter(value);
    else handleFilter(key, value);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSelectedBudget(null);
  };

  // "Sayfaya git": 1 ile son sayfa arasında olmalı
  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedBudget ? rows.findIndex((row) => row.id === selectedBudget.id) : -1;
  const selectSibling = (offset) => {
    const sibling = rows[selectedIndex + offset];
    if (!sibling) return;
    setSelectedBudget(sibling);
    reveal(sibling.id);
  };

  const formatMoney = (value) => value.toLocaleString(locale, { maximumFractionDigits: 2 });
  const tiles = [
    { key: 'receivedIncome', label: label('stats.receivedIncome'), shortLabel: label('stats.receivedIncomeShort'), value: receivedIncomeAmount, format: formatMoney, suffix: '₺', icon: LuCircleArrowDown, gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30' },
    { key: 'pendingIncome', label: label('stats.pendingIncome'), shortLabel: label('stats.pendingIncomeShort'), value: pendingIncomeAmount, format: formatMoney, suffix: '₺', icon: LuHourglass, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30' },
    { key: 'paidExpense', label: label('stats.paidExpense'), shortLabel: label('stats.paidExpenseShort'), value: paidBudgetAmount, format: formatMoney, suffix: '₺', icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-green-600 tw-shadow-emerald-500/30' },
    { key: 'pendingExpense', label: label('stats.pendingExpense'), shortLabel: label('stats.pendingExpenseShort'), value: pendingBudgetAmount, format: formatMoney, suffix: '₺', icon: LuWalletCards, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  ];

  const tabOption = (value, full, short, count) => ({
    value,
    label: (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5">
        {compactList ? short : full}
        <span className="tw-rounded-full tw-bg-slate-900/[0.07] tw-px-1.5 tw-text-[11px] tw-font-bold tw-tabular-nums dark:tw-bg-white/10">{count}</span>
      </span>
    ),
  });

  const dateRange = (
    <span title={label('toolbar.dateRangeHint')} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
        <LuCalendarRange aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
        {label('toolbar.dateRange')}
      </span>
      <span className="tw-w-[10.5rem]">
        <TextInput type="date" aria-label={label('toolbar.startDate')} value={startDateFilter} onChange={(e) => setStartDateFilter(e.target.value)} className="tw-rounded-full" />
      </span>
      <span aria-hidden="true" className="tw-text-slate-400">–</span>
      <span className="tw-w-[10.5rem]">
        <TextInput type="date" aria-label={label('toolbar.endDate')} value={endDateFilter} min={startDateFilter || ''} onChange={(e) => setEndDateFilter(e.target.value)} className="tw-rounded-full" />
      </span>
    </span>
  );

  const deleteName = selectedBudget?.['BütçeAdı'] || '';
  const [deleteBefore, deleteAfter] = label('delete.message', { name: '\u0007' }).split('\u0007');

  const paneProps = {
    budget: selectedBudget,
    isAdmin,
    canToggleStatus: Boolean(canToggleStatus),
    getEmployeeName,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: rows.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < rows.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedBudget(null),
    onToggleStatus: handleToggleStatus,
    onEdit: handleEditBudget,
    onDelete: handleDeleteBudget,
    onToggleActive: handleToggleActiveStatus,
  };

  const emptyLabel = isIncomeTab
    ? label(isFilterActive ? 'table.emptyIncome' : 'table.emptyIncomeNone')
    : label(isFilterActive ? 'table.emptyExpense' : 'table.emptyExpenseNone');
  const listLabel = label(isIncomeTab ? 'table.incomeLabel' : 'table.expenseLabel');

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <BudgetHeader
          onAddIncome={() => setIsAddIncomeModalOpen(true)}
          onAddExpense={() => setIsAddExpenseModalOpen(true)}
          onImportIncome={() => setIsImportIncomeModalOpen(true)}
          onImportExpense={() => setIsImportExpenseModalOpen(true)}
          onExcel={downloadExcel}
          onDeleteAll={() => setDeleteAllConfirm(true)}
        />

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={listLabel} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: [
                  tabOption('gelir', label('tabs.income'), label('tabs.incomeShort'), filteredIncome.length),
                  tabOption('gider', label('tabs.expense'), label('tabs.expenseShort'), filteredExpenses.length),
                ],
                value: activeTab,
                onChange: switchTab,
                layoutId: 'budget-list-tab',
                label: label('tabs.label'),
              }}
              columnMenu={{
                columns: Object.keys(columnDefs).map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => changeFilter(key, '')}
              onClearAll={clearFilters}
              extra={compactList ? undefined : dateRange}
            />

            {compactList ? (
              <DataCardList
                label={listLabel}
                rows={pageRows}
                renderCard={(budget) => <BudgetCardRow budget={budget} getEmployeeName={getEmployeeName} />}
                selectedId={selectedBudget?.id}
                onSelect={handleBudgetSelect}
                isLoading={isLoading}
                isBusy={isLoading && budgets.length > 0}
                emptyLabel={emptyLabel}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                key={activeTab}
                label={listLabel}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={pinnedFor(activeTab)}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(budget, key) => <BudgetCell columnKey={key} budget={budget} columns={columnDefs} getEmployeeName={getEmployeeName} />}
                getCellTitle={makeCellTitle(columnDefs, getEmployeeName)}
                selectedId={selectedBudget?.id}
                onSelect={handleBudgetSelect}
                isLoading={isLoading}
                isBusy={isLoading && budgets.length > 0}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={emptyLabel}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={rows.length}
              rangeOf={label('table.rangeOf', { total: rows.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: budgets.filter((b) => (b['GiderTipi'] === 'Gelir') === isIncomeTab).length.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading && budgets.length > 0}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <BudgetDetailPane docked height={fitHeight} {...paneProps} />}
        </div>

        <BudgetCharts budgets={budgets} isDarkMode={isDarkMode} />
      </div>

      {!dockPanel && <BudgetDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={[
          { key: START_KEY, label: label('toolbar.startDate'), type: 'date' },
          { key: END_KEY, label: label('toolbar.endDate'), type: 'date' },
          ...Object.keys(columnDefs).map((key) => ({
            key,
            label: columnLabel(key),
            type: columnDefs[key].type,
            options: columnDefs[key].type === 'select' ? optionsFor(key) : undefined,
          })),
        ]}
        filters={{ ...filters, [START_KEY]: startDateFilter, [END_KEY]: endDateFilter }}
        getFilterState={getFilterState}
        onFilterChange={changeFilter}
        onClearAll={clearFilters}
        resultCount={rows.length}
      />

      <ConfirmDialog
        isOpen={deleteConfirm}
        tone="danger"
        icon={LuTrash2}
        title={label('delete.title')}
        message={(
          <>
            {deleteBefore}
            <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{deleteName}</strong>
            {deleteAfter}
          </>
        )}
        confirmLabel={label('delete.confirm')}
        cancelLabel={label('common.giveUp')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(false)}
      />

      <ConfirmDialog
        isOpen={deleteAllConfirm}
        tone="danger"
        icon={LuTrash2}
        title={label('deleteAll.title')}
        message={label('deleteAll.message')}
        confirmLabel={isDeletingAll ? label('deleteAll.deleting') : label('deleteAll.confirm')}
        cancelLabel={label('common.giveUp')}
        busy={isDeletingAll}
        onConfirm={confirmDeleteAll}
        onCancel={() => setDeleteAllConfirm(false)}
      />

      {isAddIncomeModalOpen && (
        <AddIncomeModal
          isOpen={isAddIncomeModalOpen}
          onClose={() => setIsAddIncomeModalOpen(false)}
          onSubmit={handleAddItem}
        />
      )}
      {isAddExpenseModalOpen && (
        <AddExpenseModal
          isOpen={isAddExpenseModalOpen}
          onClose={() => setIsAddExpenseModalOpen(false)}
          onSubmit={handleAddItem}
        />
      )}

      {isImportIncomeModalOpen && (
        <ImportIncomeExcelModal
          isOpen={isImportIncomeModalOpen}
          onClose={() => setIsImportIncomeModalOpen(false)}
          onImported={fetchBudgets}
        />
      )}

      {isImportExpenseModalOpen && (
        <ImportExpenseExcelModal
          isOpen={isImportExpenseModalOpen}
          onClose={() => setIsImportExpenseModalOpen(false)}
          onImported={fetchBudgets}
        />
      )}

      {isEditIncomeModalOpen && editingBudget && (
        <EditIncomeModal
          isOpen={isEditIncomeModalOpen}
          onClose={() => { setIsEditIncomeModalOpen(false); setEditingBudget(null); }}
          onSubmit={handleUpdateBudget}
          budgetData={editingBudget}
        />
      )}
      {isEditExpenseModalOpen && editingBudget && (
        <EditExpenseModal
          isOpen={isEditExpenseModalOpen}
          onClose={() => { setIsEditExpenseModalOpen(false); setEditingBudget(null); }}
          onSubmit={handleUpdateBudget}
          budgetData={editingBudget}
        />
      )}
    </>
  );
};

export default BudgetManagementPage;
